import { z } from 'zod';
import { apiClient } from '../lib/api';
import { buildApiUrl } from '../utils/apiUrl';
import {
	ApiResponseSchema,
	PaginatedApiResponseSchema,
} from '../schemas';
import { UniversalChartDataSchema, NivoLineChartDataSchema, NivoStackedBarChartDataSchema } from '../schemas/chartSchema';

export const AdmissionMethodSchema = z.object({
	maPhuongThuc: z.string(),
	tenPhuongThuc: z.string().nullable(),
	doUuTien: z.number().int(),
	createdAt: z.string(),
	updatedAt: z.string(),
}).strip();

export const CreateAdmissionMethodSchema = AdmissionMethodSchema.pick({
	maPhuongThuc: true,
	tenPhuongThuc: true,
	doUuTien: true,
});

export const UpdateAdmissionMethodSchema = AdmissionMethodSchema.pick({
	tenPhuongThuc: true,
	doUuTien: true,
});

export const AdmissionMethodYearStatsSchema = z.object({
	maPhuongThuc: z.string(),
	tenPhuongThuc: z.string(),
	yearData: z.record(z.string(), z.number())
}).strip();

export const AdmissionMethodStatsResponseSchema = z.object({
	years: z.array(z.number()),
	stats: z.array(AdmissionMethodYearStatsSchema),
	total: z.number().optional()
}).strip();

export const AdmissionMethodOverviewStatsSchema = z.object({
	totalMethods: z.number(),
	methodDetails: z.array(z.object({
		maPhuongThuc: z.string(),
		tenPhuongThuc: z.string(),
		studentCount: z.number(),
		doUuTien: z.number()
	}).strip())
}).strip();

export const admissionMethodApi = {
	async getAdmissionMethods(searchParams: URLSearchParams) {
		const url = buildApiUrl('/admission-methods', searchParams);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(AdmissionMethodSchema.array()).parse(response.data);
	},

	async getAdmissionMethodById(id: string) {
		const response = await apiClient.get(`/admission-methods/${id}`);
		return ApiResponseSchema(AdmissionMethodSchema).parse(response.data);
	},

	async createAdmissionMethod(data: z.infer<typeof CreateAdmissionMethodSchema>) {
		const response = await apiClient.post('/admission-methods', data);
		return ApiResponseSchema(AdmissionMethodSchema).parse(response.data);
	},

	async updateAdmissionMethod(id: string, data: z.infer<typeof UpdateAdmissionMethodSchema>) {
		const response = await apiClient.put(`/admission-methods/${id}`, data);
		return ApiResponseSchema(AdmissionMethodSchema).parse(response.data);
	},

	async deleteAdmissionMethod(id: string): Promise<{ message: string }> {
		const response = await apiClient.delete<{ message: string }>(`/admission-methods/${id}`);
		return response.data;
	},

	async getAdmissionMethodStats(params?: URLSearchParams) {
		const url = buildApiUrl('/admission-methods/stats/admission', params);
		const response = await apiClient.get(url);
		return PaginatedApiResponseSchema(AdmissionMethodStatsResponseSchema).parse(response.data);
	},

	async getAdmissionMethodOverview(params?: URLSearchParams) {
		const url = buildApiUrl('/admission-methods/stats/overview', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(AdmissionMethodOverviewStatsSchema).parse(response.data);
	},

	async getLineChartData(params?: URLSearchParams) {
		const url = buildApiUrl('/admission-methods/stats/chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(UniversalChartDataSchema).parse(response.data);
	},

	async getNivoLineChartData(params?: URLSearchParams) {
		const url = buildApiUrl('/admission-methods/stats/nivo-chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(NivoLineChartDataSchema).parse(response.data);
	},

	async getNivoStackedBarChartData(params?: URLSearchParams) {
		const url = buildApiUrl('/admission-methods/stats/nivo-stacked-bar-chart-data', params);
		const response = await apiClient.get(url);
		return ApiResponseSchema(NivoStackedBarChartDataSchema).parse(response.data);
	},

	async exportToExcel(params?: URLSearchParams): Promise<void> {
		return apiClient.exportToExcel('/admission-methods/export/excel', params);
	},
};

// Export type for use in other files
export type AdmissionMethod = z.infer<typeof AdmissionMethodSchema>;
